import lineIntersect from "@turf/line-intersect";
import { Feature, LineString } from "geojson";
import { LatLng } from "leaflet";

// Simplified version of Maplibre's
interface LngLat {
  /**
   * Longitude, measured in degrees.
   */
  lng: number;
  /**
   * Latitude, measured in degrees.
   */
  lat: number;
}

/* Helper functions */

// The Following function uses the turf library to calculate if lines intersect
// It takes an array of old points and a seperate new point and returns an array of points where the new point caused intersections with the old points.
/*
   For example in below illistration
   when 1 2 and 3 are coordinates of existing points
   and 4 is the coordinate of a new points
   calling "getPointsWhereNewPointInLineCrossesExistingLines ( 4, [1,2,3]) " will return the: [coordinates_of_X]


   4 (new point)
   \
   \
   1_____________X_______2
   \     /
   \   /
   \ /
   3

*/

type Point = [number, number] | LatLng | LngLat;

export function doesNewPointCrossExistingLines(newPoint: Point, existingPoints: Point[]): boolean {
  if (existingPoints.length <= 2) {
    return false; // Can't intersect
  }

  const newLine = pointsToLineString([existingPoints[existingPoints.length - 1], newPoint]);

  // since the last line part of the existing points and the new
  // linepart depart from a common point (namely the last point of the
  // existing points) they cannot intersect
  // therefore we end the loop at existingPoints.length - 2 instead of 1.
  for (let startIndex = 0; startIndex < existingPoints.length - 2; startIndex++) {
    const existingLine = pointsToLineString([
      existingPoints[startIndex],
      existingPoints[startIndex + 1],
    ]);
    // use turf to calculate the intersections between the newline and the existing line
    const intersection = lineIntersect(existingLine, newLine);
    if (intersection.features.length) return true;
  }
  return false;
}

export function doesNewPointCrossPolygon(
  newPoint: Point,
  existingPoints: Point[],
  polygon: Point[]
): boolean {
  if (polygon.length < 3) {
    return false; // No polygon to intersect
  }

  if (existingPoints.length === 0) {
    return false; // No line to intersect
  }

  const newLine = pointsToLineString([existingPoints[existingPoints.length - 1], newPoint]);

  const existingPolygonLines = polygon.map((line, i) => {
    if (i === polygon.length - 1) {
      return pointsToLineString([line, polygon[0]]);
    }
    return pointsToLineString([line, polygon[i + 1]]);
  });

  const isIntersect = !!existingPolygonLines.find((line) => lineIntersect(line, newLine));
  return isIntersect;
}

export function pointsToLineString(points: Point[]): Feature<LineString> {
  return {
    type: "Feature",
    geometry: {
      type: "LineString",
      coordinates: points.map((point) => (Array.isArray(point) ? point : [point.lng, point.lat])),
    },
    properties: {},
  };
}
